/**
 * BrowseShopsInline — Lightweight grid of public shops embedded in the home page.
 * Shows poster cards in a 2-column grid with category chips.
 * Links to /browse for the full experience with preview animations.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useShopStore, getEnrichedShops, type EnrichedPublicShop } from '../../stores/shopStore'
import { apiUrl } from '../../config'
import './BrowseShopsInline.css'

export default function BrowseShopsInline() {
  const navigate = useNavigate()
  const { t } = useTranslation('entrance')
  const { t: tShops } = useTranslation('shops')
  const { publicShops, publicShopsLoading, publicDatasetsLoading, publicDatasets, fetchPublicShops, fetchPublicDatasets } = useShopStore()
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  useEffect(() => {
    if (publicShops.length === 0) fetchPublicShops()
    if (publicDatasets.length === 0) fetchPublicDatasets()
  }, [])

  const enrichedShops = useMemo(() => getEnrichedShops(), [publicShops, publicDatasets])

  const categories = useMemo(() => {
    const cats = new Set(publicShops.map((s) => s.category).filter(Boolean))
    return [...cats].sort()
  }, [publicShops])

  const filtered = useMemo(() => {
    if (!selectedCategory) return enrichedShops
    return enrichedShops.filter((s) => s.category?.toLowerCase() === selectedCategory.toLowerCase())
  }, [enrichedShops, selectedCategory])

  const isLoading = publicShopsLoading || publicDatasetsLoading

  const handleShopClick = (shop: EnrichedPublicShop) => {
    if (shop.has3DViewer && shop.dataset) {
      window.location.href = `/?shopid=${shop.dataset.id}`
    } else {
      navigate(`/shop/${shop.id}`)
    }
  }

  if (isLoading && publicShops.length === 0) {
    return (
      <section className="browse-inline-section">
        <div className="browse-inline-header">
          <h2 className="browse-inline-title">
            <span className="browse-inline-icon">🛍️</span>
            {t('allShops')}
          </h2>
        </div>
        <div className="browse-inline-loading">{tShops('loadingShops')}</div>
      </section>
    )
  }

  if (enrichedShops.length === 0) return null

  return (
    <section className="browse-inline-section">
      <div className="browse-inline-header">
        <h2 className="browse-inline-title">
          <span className="browse-inline-icon">🛍️</span>
          {t('allShops')}
        </h2>
        <button className="browse-inline-view-all" onClick={() => navigate('/browse')}>
          {t('viewAll')} →
        </button>
      </div>

      {/* Category chips */}
      {categories.length > 1 && (
        <div className="browse-inline-chips">
          <button
            className={`browse-inline-chip ${!selectedCategory ? 'active' : ''}`}
            onClick={() => setSelectedCategory(null)}
          >
            {tShops('common:all')}
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`browse-inline-chip ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => setSelectedCategory(selectedCategory === cat ? null : cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Shop grid */}
      <div className="browse-inline-grid">
        {filtered.map((shop) => (
          <BrowseShopCard key={shop.id} shop={shop} onClick={() => handleShopClick(shop)} />
        ))}
      </div>
    </section>
  )
}

/* ─── Lightweight shop card ─── */

function BrowseShopCard({ shop, onClick }: { shop: EnrichedPublicShop; onClick: () => void }) {
  const [imgError, setImgError] = useState(false)
  const token = localStorage.getItem('dukkan_token')
  const posterUrl = apiUrl(`/api/shops/${shop.id}/poster${token ? `?token=${encodeURIComponent(token)}` : ''}`)

  return (
    <button className="browse-inline-card" onClick={onClick}>
      <div className="browse-inline-card-img-wrap">
        {!imgError ? (
          <img
            className="browse-inline-card-img"
            src={posterUrl}
            alt={shop.name}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="browse-inline-card-placeholder">🏪</div>
        )}
        <div className="browse-inline-card-overlay" />
        {shop.cameraCount != null && (
          <div className="browse-inline-card-badge">📷 {shop.cameraCount}</div>
        )}
      </div>
      <div className="browse-inline-card-info">
        <span className="browse-inline-card-name">{shop.name}</span>
        {shop.category && (
          <span className="browse-inline-card-category">{shop.category}</span>
        )}
      </div>
    </button>
  )
}
